import { DeleteOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { monoFont } from '@medcom/shared-ui/styles/globalCss.ts';
import { antdTokenFilter } from '@medcom/shared-ui/utils/tokenSearch.ts';
import { Button, Card, Checkbox, Dropdown, Form, type FormInstance, Input, Select, Space, Typography } from 'antd';
import { useWatch } from 'antd/es/form/Form';
import type React from 'react';
import { useGetAllThemes } from '../../../api/theme.queries.ts';
import { EditableTagGroup } from '../../../components/EditableTagGroup.tsx';
import { type Account, type AccountAuthz, LabId } from '../../../generated/openapi/befunde-api';

export type FormModel = Omit<Account, 'id' | 'userCount' | 'themeName'>;

export const AccountForm: React.FC<{
  form: FormInstance<FormModel>;
  initialValues?: FormModel;
  onFinish: (values: FormModel) => void;
}> = ({ form, initialValues, onFinish }) => {
  const authz = useWatch<AccountAuthz[]>('authz', { form, preserve: true });
  const { data: themes } = useGetAllThemes();

  return (
    <Form<FormModel> layout="vertical" form={form} onFinish={() => onFinish(form.getFieldsValue(true))} initialValues={initialValues}>
      <Form.Item name="name" label="Name" rules={[{ type: 'string', required: true, whitespace: false }]}>
        <Input autoFocus />
      </Form.Item>
      <Form.Item name="disabled" valuePropName="checked">
        <Checkbox>Deaktiviert</Checkbox>
      </Form.Item>
      <Form.Item name="themeId" label="Theme">
        <Select placeholder="Theme zuweisen" style={{ width: '300px' }} allowClear showSearch={{ filterOption: antdTokenFilter }}>
          {themes?.items?.map(it => {
            return (
              <Select.Option key={it.id} value={it.id} search={it.name}>
                {it.name}
              </Select.Option>
            );
          })}
        </Select>
      </Form.Item>
      <Form.List
        name="authz"
        rules={[
          {
            validator: (_, values) => {
              if (!values || values.length < 1) {
                return Promise.reject('Mind. ein Labor angeben');
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        {(fields, { add, remove }, { errors }) => (
          <Space orientation="vertical" style={{ width: '100%' }}>
            <Dropdown
              menu={{
                items: Object.values(LabId).map(lab => ({
                  key: lab,
                  label: lab,
                  disabled: authz?.map(it => it.labId as string)?.includes(lab) ?? false,
                  onClick: () =>
                    add({
                      labId: lab,
                      identifiers: [],
                    }),
                })),
              }}
              placement="bottomLeft"
              trigger={['click']}
            >
              <Button icon={<PlusCircleOutlined />}>Labor hinzufügen</Button>
            </Dropdown>
            {fields.map(({ key, name, ...restField }, index) => (
              <Card
                size="small"
                title={
                  Object.values(LabId).find(it => it === (authz ?? [])[index]?.labId) ? (
                    <span className={monoFont}>{(authz ?? [])[index]?.labId}</span>
                  ) : (
                    `Labor ${index + 1}`
                  )
                }
                extra={<Button danger size="small" icon={<DeleteOutlined />} type="text" onClick={() => remove(index)} />}
                style={{ width: '100%' }}
                key={key}
              >
                <Form.Item
                  {...restField}
                  name={[name, 'identifiers']}
                  label="Identifikatoren"
                  valuePropName="tags"
                  rules={[{ type: 'array' }]}
                  className={css`
                    margin-bottom: 0;
                  `}
                >
                  <EditableTagGroup addLabel="Identifikator hinzufügen" inputWidth="170px" mono />
                </Form.Item>
              </Card>
            ))}
            <Form.ErrorList
              errors={errors.map((it, idx) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: no unique/stable key available
                <Typography.Text key={idx} type="danger">
                  {it}
                </Typography.Text>
              ))}
            />
          </Space>
        )}
      </Form.List>
      {/* hack for enter button support */}
      <Button htmlType="submit" hidden />
    </Form>
  );
};
